'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { PageErrorBoundary } from '@/components/ErrorBoundary';
import { SkeletonTable } from '@/components/Skeleton';
import { useToast } from '@/components/Toast';

interface WatchAlert {
  id: string;
  markName: string;
  applicantName: string;
  applicationNumber: string;
  filingDate: string;
  trademarkClass: number;
  goodsServices: string;
  similarityScore: number;
  status: string;
  alertType: 'new_filing' | 'status_change' | 'opposition_window';
  isRead: boolean;
  watchedMark: string;
}

interface WatchConfig {
  markName: string;
  classes: number[];
  alertThreshold: number;
  emailAlerts: boolean;
  whatsappAlerts: boolean;
}

const demoAlerts: WatchAlert[] = [
  {
    id: '1', markName: 'ELYSIAN', applicantName: 'Elysian Beauty Pvt Ltd',
    applicationNumber: 'TM-2026-4521890', filingDate: '2026-08-20',
    trademarkClass: 3, goodsServices: 'Perfumes, cosmetics, skincare',
    similarityScore: 91, status: 'examination', alertType: 'new_filing',
    isRead: false, watchedMark: 'ELYSIUM',
  },
  {
    id: '2', markName: 'ELYSIUM BEAUTY', applicantName: 'Elysium Beauty LLP',
    applicationNumber: 'TM-2026-4498231', filingDate: '2026-08-15',
    trademarkClass: 3, goodsServices: 'Cosmetics, face creams, body lotions',
    similarityScore: 97, status: 'pending', alertType: 'new_filing',
    isRead: false, watchedMark: 'ELYSIUM',
  },
  {
    id: '3', markName: 'ELYSIUM GOLD', applicantName: 'Elysium Wellness Pvt Ltd',
    applicationNumber: 'TM-2025-3312456', filingDate: '2025-11-10',
    trademarkClass: 3, goodsServices: 'Luxury perfumes, essential oils',
    similarityScore: 85, status: 'registered', alertType: 'status_change',
    isRead: true, watchedMark: 'ELYSIUM',
  },
  {
    id: '4', markName: 'ELYSIUM SCENTS', applicantName: 'Elysium Scents Co',
    applicationNumber: 'TM-2026-4501234', filingDate: '2026-08-18',
    trademarkClass: 35, goodsServices: 'Retail services for perfumes',
    similarityScore: 78, status: 'pending', alertType: 'opposition_window',
    isRead: false, watchedMark: 'ELYSIUM',
  },
  {
    id: '5', markName: 'ELYSIUM LABS', applicantName: 'Elysium Laboratories',
    applicationNumber: 'TM-2026-4489012', filingDate: '2026-08-10',
    trademarkClass: 5, goodsServices: 'Pharmaceutical preparations',
    similarityScore: 72, status: 'examination', alertType: 'new_filing',
    isRead: true, watchedMark: 'ELYSIUM',
  },
];

const alertTypeConfig = {
  new_filing: { label: 'New Filing', color: 'bg-danger-light text-danger', icon: '🆕' },
  status_change: { label: 'Status Change', color: 'bg-warning/10 text-warning', icon: '🔄' },
  opposition_window: { label: 'Opposition Window', color: 'bg-ai-light text-ai', icon: '⚖️' },
};

const statusColors: Record<string, string> = {
  registered: 'bg-success-light text-success',
  examination: 'bg-warning/10 text-warning',
  pending: 'bg-ai-light text-ai',
  refused: 'bg-danger-light text-danger',
};

export default function TrademarkWatchPage() {
  const [alerts, setAlerts] = useState<WatchAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterRead, setFilterRead] = useState<'all' | 'unread'>('all');
  const [watchConfig, setWatchConfig] = useState<WatchConfig>({
    markName: 'ELYSIUM',
    classes: [3, 35, 44],
    alertThreshold: 70,
    emailAlerts: true,
    whatsappAlerts: false,
  });
  const [showConfig, setShowConfig] = useState(false);
  const [scanning, setScanning] = useState(false);
  const { success, info } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setAlerts(demoAlerts);
      setLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const markAsRead = useCallback((id: string) => {
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, isRead: true } : a));
  }, []);

  const markAllRead = () => {
    setAlerts(prev => prev.map(a => ({ ...a, isRead: true })));
    success('All alerts marked as read');
  };

  const runScan = async () => {
    setScanning(true);
    await new Promise(r => setTimeout(r, 2000));
    setScanning(false);
    info('Scan complete', 'No new similar filings found in the last 24 hours.');
  };

  const filteredAlerts = alerts.filter(a => {
    if (filterType !== 'all' && a.alertType !== filterType) return false;
    if (filterRead === 'unread' && a.isRead) return false;
    return true;
  });

  const unreadCount = alerts.filter(a => !a.isRead).length;
  const highRiskCount = alerts.filter(a => a.similarityScore >= 85).length;

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/trademark/watch" />

        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Link href="/trademark" className="text-[12px] text-ink-muted hover:text-ink transition-colors">Trademark</Link>
                  <span className="text-ink-subtle">›</span>
                  <span className="text-[12px] text-ink font-medium">Watch Monitor</span>
                </div>
                <h1 className="text-2xl font-bold text-ink">Trademark Watch</h1>
                <p className="text-[14px] text-ink-muted mt-0.5">Monitor new similar filings and get alerted before it's too late.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={runScan}
                  disabled={scanning}
                  className="flex items-center gap-2 bg-white border border-border text-[13px] font-medium text-ink-muted px-4 py-2.5 rounded-xl hover:border-ai/30 hover:text-ink transition-all disabled:opacity-50"
                >
                  {scanning ? (
                    <span className="w-3.5 h-3.5 border-2 border-ai/30 border-t-ai rounded-full animate-spin" />
                  ) : '🔍'}
                  {scanning ? 'Scanning...' : 'Scan Now'}
                </button>
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className="flex items-center gap-2 bg-ai text-white text-[13px] font-semibold px-4 py-2.5 rounded-xl hover:bg-ai-dark transition-all"
                >
                  ⚙️ Configure Watch
                </button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Unread Alerts', value: unreadCount, color: 'text-danger', bg: 'bg-danger-light' },
                { label: 'High Risk', value: highRiskCount, color: 'text-warning', bg: 'bg-warning/10' },
                { label: 'Total Alerts', value: alerts.length, color: 'text-ink', bg: 'bg-surface-3' },
                { label: 'Watched Marks', value: 1, color: 'text-ai', bg: 'bg-ai-light' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-xl border border-border p-4">
                  <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-2">{stat.label}</p>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Watch Config Panel */}
            {showConfig && (
              <div className="bg-white rounded-xl border border-border p-5 mb-6">
                <h3 className="text-[14px] font-semibold text-ink mb-4">Watch Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Watching Mark</label>
                    <input
                      value={watchConfig.markName}
                      onChange={e => setWatchConfig(prev => ({ ...prev, markName: e.target.value }))}
                      className="w-full bg-surface-2 border border-border rounded-xl px-4 py-2.5 text-[14px] text-ink focus:outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/40"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Similarity Threshold (%)</label>
                    <input
                      type="range"
                      min={50}
                      max={100}
                      value={watchConfig.alertThreshold}
                      onChange={e => setWatchConfig(prev => ({ ...prev, alertThreshold: parseInt(e.target.value) }))}
                      className="w-full mt-2"
                    />
                    <div className="flex justify-between text-[11px] text-ink-muted mt-1">
                      <span>50%</span>
                      <span className="font-semibold text-ai">{watchConfig.alertThreshold}%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Alert Channels</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={watchConfig.emailAlerts} onChange={e => setWatchConfig(prev => ({ ...prev, emailAlerts: e.target.checked }))} className="rounded" />
                        <span className="text-[13px] text-ink">Email alerts</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={watchConfig.whatsappAlerts} onChange={e => setWatchConfig(prev => ({ ...prev, whatsappAlerts: e.target.checked }))} className="rounded" />
                        <span className="text-[13px] text-ink">WhatsApp alerts</span>
                        <span className="text-[10px] text-ink-muted">(requires WHATSAPP_API_KEY)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-ink-muted uppercase tracking-wide mb-2">Watching Classes</label>
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 5, 9, 25, 35, 41, 42, 44, 45].map(cls => (
                        <button
                          key={cls}
                          onClick={() => setWatchConfig(prev => ({
                            ...prev,
                            classes: prev.classes.includes(cls) ? prev.classes.filter(c => c !== cls) : [...prev.classes, cls],
                          }))}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                            watchConfig.classes.includes(cls) ? 'bg-ai text-white' : 'bg-surface-3 text-ink-muted hover:text-ink'
                          }`}
                        >
                          Class {cls}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => { setShowConfig(false); success('Watch configuration saved'); }}
                    className="bg-ai text-white text-[13px] font-semibold px-5 py-2 rounded-xl hover:bg-ai-dark transition-all"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl border border-border p-4 mb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilterRead(filterRead === 'unread' ? 'all' : 'unread')}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${filterRead === 'unread' ? 'bg-danger text-white' : 'bg-surface-2 text-ink-muted hover:text-ink'}`}
                  >
                    Unread only {unreadCount > 0 && `(${unreadCount})`}
                  </button>
                  {(['all', 'new_filing', 'status_change', 'opposition_window'] as const).map(type => (
                    <button
                      key={type}
                      onClick={() => setFilterType(type)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${filterType === type ? 'bg-ai text-white' : 'bg-surface-2 text-ink-muted hover:text-ink'}`}
                    >
                      {type === 'all' ? 'All' : alertTypeConfig[type].label}
                    </button>
                  ))}
                </div>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-[12px] font-medium text-ai hover:text-ai-dark transition-colors">
                    Mark all read
                  </button>
                )}
              </div>
            </div>

            {/* Alerts List */}
            {loading ? (
              <SkeletonTable rows={5} />
            ) : filteredAlerts.length === 0 ? (
              <div className="bg-white rounded-xl border border-border p-12 text-center">
                <div className="w-14 h-14 bg-success-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">✓</span>
                </div>
                <h3 className="text-[15px] font-semibold text-ink mb-2">No alerts found</h3>
                <p className="text-[13px] text-ink-muted">No similar trademark filings match your current filters.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAlerts.map(alert => (
                  <div
                    key={alert.id}
                    onClick={() => markAsRead(alert.id)}
                    className={`bg-white rounded-xl border p-5 cursor-pointer transition-all hover:shadow-sm ${
                      !alert.isRead ? 'border-ai/30 bg-ai/[0.01]' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {!alert.isRead && <span className="w-2 h-2 bg-ai rounded-full flex-shrink-0 mt-1.5" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1.5">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${alertTypeConfig[alert.alertType].color}`}>
                              {alertTypeConfig[alert.alertType].icon} {alertTypeConfig[alert.alertType].label}
                            </span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusColors[alert.status] || 'bg-surface-3 text-ink-muted'}`}>
                              {alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}
                            </span>
                            <span className="text-[10px] text-ink-muted bg-surface-3 px-2 py-0.5 rounded-full">Class {alert.trademarkClass}</span>
                          </div>
                          <p className="text-[15px] font-bold text-ink">{alert.markName}</p>
                          <p className="text-[12px] text-ink-muted">{alert.applicantName}</p>
                          <p className="text-[12px] text-ink-muted mt-1">{alert.goodsServices}</p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            <span className="text-[11px] text-ink-subtle">App: {alert.applicationNumber}</span>
                            <span className="text-[11px] text-ink-subtle">Filed: {new Date(alert.filingDate).toLocaleDateString('en-IN')}</span>
                            <span className="text-[11px] text-ink-subtle">Watching: <span className="font-semibold text-ink">{alert.watchedMark}</span></span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`text-2xl font-bold ${alert.similarityScore >= 90 ? 'text-danger' : alert.similarityScore >= 75 ? 'text-warning' : 'text-ink'}`}>
                          {alert.similarityScore}%
                        </div>
                        <p className="text-[10px] text-ink-muted">similarity</p>
                        {alert.similarityScore >= 85 && (
                          <span className="text-[10px] font-bold text-danger bg-danger-light px-2 py-0.5 rounded-full mt-1 block">High Risk</span>
                        )}
                      </div>
                    </div>
                    {alert.alertType === 'opposition_window' && (
                      <div className="mt-3 p-3 bg-warning/8 border border-warning/20 rounded-lg">
                        <p className="text-[12px] font-semibold text-warning">⚖️ Opposition Window Open</p>
                        <p className="text-[11px] text-ink-muted mt-0.5">You have 4 months from the publication date to file an opposition. Consult a trademark attorney immediately.</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <p className="text-[11px] text-ink-subtle text-center mt-6">
              Trademark Watch monitors new filings every 24 hours. Live data requires MCA/IP India API credentials.
            </p>
          </div>
        </main>
      </div>
    </PageErrorBoundary>
  );
}
